
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { GalleryPhoto } from "./carousel/types";
import { FileDown, Download, Loader2 } from "lucide-react";
import { generateGalleryPdf } from "@/utils/pdf/galleryPdfGenerator";
import { toast } from "sonner";

interface GalleryPdfExportProps {
  photos: GalleryPhoto[];
  collectionTitle?: string;
  fetchPhotos?: () => Promise<GalleryPhoto[]>;
}

export function GalleryPdfExport({ photos, collectionTitle, fetchPhotos }: GalleryPdfExportProps) {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [includeMilestones, setIncludeMilestones] = useState(true);
  const [includePhotoDates, setIncludePhotoDates] = useState(true);
  const [includeDecorations, setIncludeDecorations] = useState(true);

  const handleGeneratePdf = async () => {
    setIsGenerating(true);
    
    try {
      // Show toast to indicate process started
      toast.success(t('gallery.generating', language));
      
      // If fetchPhotos is provided, use it to get the latest photos
      const photosToExport = fetchPhotos ? await fetchPhotos() : photos;
      
      if (photosToExport.length === 0) {
        toast.error(t('gallery.noPhotos', language));
        setIsGenerating(false);
        return;
      }
      
      console.log(`Starting PDF generation with ${photosToExport.length} photos`);
      
      // Get proper title for PDF
      let pdfTitle = t('gallery.photoCollection', language);
      
      // If collection title is provided, try to get the translation or fallback to the original
      if (collectionTitle) {
        // First try to find a direct translation key for the collection
        const directKey = `gallery.${collectionTitle.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
        const translatedTitle = t(directKey, language);
        
        // If the translation returns the key itself (not found), use the original with first letter capitalized
        if (translatedTitle === directKey) {
          pdfTitle = collectionTitle.charAt(0).toUpperCase() + collectionTitle.slice(1);
        } else {
          pdfTitle = translatedTitle;
        }
      }
      
      // Generate PDF with options
      await generateGalleryPdf(
        photosToExport, 
        pdfTitle, 
        { includeMilestones, includePhotoDates, includeDecorations }
      );
      
      setOpen(false);
      toast.success(t('gallery.pdfGenerated', language));
    } catch (error) {
      console.error("Error in PDF export process:", error);
      toast.error(t('gallery.pdfGenerationFailed', language));
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="ml-2">
          <FileDown className="mr-1" size={16} />
          {t('gallery.exportPdf', language)}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('gallery.exportPdfTitle', language)}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-4">
            {t('gallery.exportPdfDescription', language)}
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="milestones" 
                checked={includeMilestones} 
                onCheckedChange={(checked) => setIncludeMilestones(!!checked)} 
              />
              <Label htmlFor="milestones">{t('gallery.includeMilestones', language)}</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="dates" 
                checked={includePhotoDates} 
                onCheckedChange={(checked) => setIncludePhotoDates(!!checked)} 
              />
              <Label htmlFor="dates">{t('gallery.includePhotoDates', language)}</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="decorations" 
                checked={includeDecorations} 
                onCheckedChange={(checked) => setIncludeDecorations(!!checked)} 
              />
              <Label htmlFor="decorations">{t('gallery.includeDecorations', language)}</Label>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <Button onClick={handleGeneratePdf} disabled={isGenerating}>
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('gallery.generating', language)}
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  {t('gallery.downloadPdf', language)}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
